/**
 * Roles describe people; permissions describe actions. Enforce permissions on the server for
 * every read path (lists, search, Ask, exports), not only on buttons.
 */
export const ROLES = ["owner", "manager", "hand", "vet", "accountant", "viewer"] as const;
export type Role = (typeof ROLES)[number];

export type Permission =
  | "read"          // cattle, places, work, history
  | "record"        // capture and file events
  | "review"        // decide proposals from anyone
  | "manage"        // edit records, jobs, settings, corrections of others' work
  | "finance.read"
  | "finance.write"
  | "people"        // invite, change roles, sharing
  | "data";         // imports, full exports, restore

const GRANTS: Record<Role, readonly Permission[]> = {
  owner: ["read", "record", "review", "manage", "finance.read", "finance.write", "people", "data"],
  manager: ["read", "record", "review", "manage", "finance.read", "finance.write", "data"],
  hand: ["read", "record"],
  vet: ["read", "record"],
  accountant: ["read", "finance.read", "finance.write"],
  viewer: ["read"],
};

export function can(role: Role | null | undefined, permission: Permission): boolean {
  return !!role && GRANTS[role].includes(permission);
}

export const ROLE_LABELS: Record<Role, string> = {
  owner: "Owner",
  manager: "Manager",
  hand: "Ranch hand",
  vet: "Veterinarian",
  accountant: "Bookkeeper",
  viewer: "Viewer",
};

export const ROLE_DESCRIPTIONS: Record<Role, string> = {
  owner: "Everything, including people, access and your data.",
  manager: "Runs the ranch: records, reviews, jobs, finances and settings.",
  hand: "Records work and sees the herd. Their own notes wait for a manager when unsure.",
  vet: "Sees the herd and records health work.",
  accountant: "Sees and records finances. No cattle edits.",
  viewer: "Can look but not change anything. No finances.",
};
